import { buildApiUrl } from './apiUrl';
import {
  fetchSheetData as requestSheet,
  fetchSheets as requestSheets,
  fetchSheetsSnapshot as requestSnapshot,
} from './sheetsApi';

const STORAGE_PREFIX = 'church.sheet-cache.v1.';
const MAX_STORAGE_BYTES = 4 * 1024 * 1024;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const WRITE_DELAY_MS = 250;
const LIST_RESOURCE = 'list';
const caches = new Map();

function getStorage() {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function storageKeys(storage) {
  const keys = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  return keys;
}

function removeStored(key) {
  try {
    getStorage()?.removeItem(key);
  } catch {
    // Storage can be disabled or become unavailable after the page loads.
  }
}

function identityHash(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
  }
  // The full identity is also checked in every record; hash collisions are misses.
  return (hash >>> 0).toString(36);
}

function getSessionIdentity(accessToken) {
  const payload = String(accessToken || '').split('.')[1] || '';
  let claims = null;
  try {
    const encoded = payload.replace(/-/g, '+').replace(/_/g, '/');
    claims = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
  } catch {
    // Unrecognized tokens remain memory-only.
  }
  let endpoint = buildApiUrl('/api/sheets-admin');
  try {
    endpoint = new URL(endpoint, window.location.href).href;
  } catch {
    // The relative endpoint is sufficient when running without a browser.
  }
  return {
    identity: JSON.stringify([endpoint, payload]),
    persistent: claims?.role === 'main_admin' && Number(claims?.level) === 1,
    tokenExpiresAt: Number(claims?.exp) > 0 ? Number(claims.exp) * 1000 : Infinity,
  };
}

function hasSensitiveData(name, data) {
  const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (/contrasena|password/.test(normalize(name))) return true;
  return (data?.headers || []).some((header) => (
    /contrasena|password|secret|secreto|hash|token/.test(normalize(header))
  ));
}

function validData(resource, data) {
  return resource === LIST_RESOURCE
    ? Array.isArray(data?.sheets) && data.sheets.every((sheet) => typeof sheet?.title === 'string')
    : Array.isArray(data?.headers)
      && data.headers.every((header) => header === null || ['string', 'number', 'boolean'].includes(typeof header))
      && Array.isArray(data?.rows) && data.rows.every(Array.isArray);
}

function invalidatedError() {
  const error = new Error('La lectura de la hoja fue reemplazada por una actualización.');
  error.name = 'AbortError';
  error.code = 'SHEET_CACHE_INVALIDATED';
  return error;
}

export function createSheetCache(accessToken) {
  const session = getSessionIdentity(accessToken);
  const registryKey = `${session.identity}\n${accessToken || ''}`;
  if (caches.has(registryKey)) return caches.get(registryKey).api;

  const entries = new Map();
  const loadedResources = new Set();
  const versions = new Map();
  const inFlight = new Map();
  const pendingWrites = new Map();
  const storageScope = `${STORAGE_PREFIX}${identityHash(session.identity)}.`;
  let writeTimer = null;
  let snapshotFlight = null;
  let epoch = 0;
  let invalidationRevision = 0;
  let sequence = 0;
  let retired = false;
  let allowStorageHydration = true;

  const resourceKey = (name) => `sheet:${name}`;
  const storedKey = (resource) => `${storageScope}${encodeURIComponent(resource)}`;
  const canPersist = (resource, data) => session.persistent
    && (resource === LIST_RESOURCE || !hasSensitiveData(resource.slice(6), data));

  function flushWrites() {
    writeTimer = null;
    if (retired || pendingWrites.size === 0) return;
    const writes = [...pendingWrites.entries()];
    pendingWrites.clear();
    const storage = getStorage();
    if (!storage) return;

    try {
      const persisted = new Map();
      let usedBytes = 0;
      for (const key of storageKeys(storage)) {
        const raw = storage.getItem(key);
        let record;
        try { record = JSON.parse(raw); } catch { record = null; }
        if (!record || !(record.expiresAt > Date.now())) {
          storage.removeItem(key);
          continue;
        }
        const bytes = 2 * (key.length + raw.length);
        persisted.set(key, { bytes, savedAt: Number(record.savedAt) || 0 });
        usedBytes += bytes;
      }

      const evictOldest = () => {
        let oldestKey;
        let oldestTime = Infinity;
        for (const [key, record] of persisted) {
          if (record.savedAt < oldestTime) {
            oldestKey = key;
            oldestTime = record.savedAt;
          }
        }
        if (!oldestKey) return false;
        storage.removeItem(oldestKey);
        usedBytes -= persisted.get(oldestKey).bytes;
        persisted.delete(oldestKey);
        return true;
      };

      for (const [resource, entry] of writes) {
        if (entries.get(resource) !== entry || entry.expiresAt <= Date.now()) continue;
        const key = storedKey(resource);
        if (persisted.has(key)) {
          storage.removeItem(key);
          usedBytes -= persisted.get(key).bytes;
          persisted.delete(key);
        }
        const raw = JSON.stringify({
          version: 1,
          identity: session.identity,
          resource,
          savedAt: entry.savedAt,
          expiresAt: entry.expiresAt,
          data: entry.data,
        });
        const bytes = 2 * (key.length + raw.length);
        if (bytes > MAX_STORAGE_BYTES) continue;
        while (usedBytes + bytes > MAX_STORAGE_BYTES && evictOldest()) { /* Evict our oldest records only. */ }
        let saved = false;
        while (!saved) {
          try {
            storage.setItem(key, raw);
            saved = true;
          } catch {
            if (!evictOldest()) break;
          }
        }
        if (saved) {
          persisted.set(key, { bytes, savedAt: entry.savedAt });
          usedBytes += bytes;
        }
      }
    } catch {
      // Quota errors and private-browser restrictions never prevent in-memory use.
    }
  }

  function get(resource) {
    if (retired) return null;
    if (!loadedResources.has(resource)) {
      loadedResources.add(resource);
      if (session.persistent && allowStorageHydration) {
        const key = storedKey(resource);
        try {
          const record = JSON.parse(getStorage()?.getItem(key) || 'null');
          if (record?.version === 1 && record.identity === session.identity
            && record.resource === resource && record.expiresAt > Date.now()
            && record.expiresAt <= record.savedAt + CACHE_TTL_MS
            && session.tokenExpiresAt > Date.now()
            && validData(resource, record.data) && canPersist(resource, record.data)) {
            entries.set(resource, { ...record, sequence: 0 });
          } else if (record) {
            removeStored(key);
          }
        } catch {
          removeStored(key);
        }
      }
    }
    const entry = entries.get(resource);
    if (!entry || entry.expiresAt <= Date.now() || session.tokenExpiresAt <= Date.now()) {
      entries.delete(resource);
      return null;
    }
    return entry.data;
  }

  function put(resource, data, requestSequence) {
    if (retired || !validData(resource, data)) return;
    if ((entries.get(resource)?.sequence || 0) > requestSequence) return;
    const savedAt = Date.now();
    const entry = {
      data,
      savedAt,
      expiresAt: Math.min(savedAt + CACHE_TTL_MS, session.tokenExpiresAt),
      sequence: requestSequence,
    };
    entries.set(resource, entry);
    loadedResources.add(resource);
    if (canPersist(resource, data)) {
      pendingWrites.set(resource, entry);
      if (writeTimer === null) writeTimer = setTimeout(flushWrites, WRITE_DELAY_MS);
    } else {
      pendingWrites.delete(resource);
      if (session.persistent) removeStored(storedKey(resource));
    }
  }

  function startResourceRead(resource, loader) {
    if (retired) return Promise.reject(invalidatedError());
    const existing = inFlight.get(resource);
    if (existing) return existing.promise;
    const readEpoch = epoch;
    const readVersion = versions.get(resource) || 0;
    const requestSequence = ++sequence;
    const read = {};
    read.promise = Promise.resolve().then(loader).then((data) => {
      if (retired || epoch !== readEpoch || (versions.get(resource) || 0) !== readVersion) {
        throw invalidatedError();
      }
      put(resource, data, requestSequence);
      return get(resource) || data;
    }).finally(() => {
      if (inFlight.get(resource) === read) inFlight.delete(resource);
    });
    inFlight.set(resource, read);
    return read.promise;
  }

  function fetchSheets({ force = false } = {}) {
    if (retired) return Promise.reject(invalidatedError());
    const cached = !force && get(LIST_RESOURCE);
    if (cached) return Promise.resolve(cached);
    if (!force && snapshotFlight) {
      return snapshotFlight.promise.then(() => get(LIST_RESOURCE));
    }
    return startResourceRead(LIST_RESOURCE, () => requestSheets(accessToken));
  }

  function fetchSheet(name, { force = false, revalidate = false } = {}) {
    if (retired) return Promise.reject(invalidatedError());
    const resource = resourceKey(name);
    const cached = !force && !revalidate && get(resource);
    if (cached) return Promise.resolve(cached);
    if (!force && snapshotFlight) {
      return snapshotFlight.promise.then(() => (
        get(resource) || startResourceRead(resource, () => requestSheet(name, accessToken))
      ));
    }
    return startResourceRead(resource, () => requestSheet(name, accessToken));
  }

  function fetchSnapshot() {
    if (retired) return Promise.reject(invalidatedError());
    if (snapshotFlight) return snapshotFlight.promise;
    const readEpoch = epoch;
    const revision = invalidationRevision;
    const requestSequence = ++sequence;
    const read = {};
    read.promise = Promise.resolve().then(() => requestSnapshot(accessToken)).then((data) => {
      if (retired || epoch !== readEpoch || revision !== invalidationRevision) {
        throw invalidatedError();
      }
      put(LIST_RESOURCE, { sheets: data.sheets || [] }, requestSequence);
      const sheetDataByName = {};
      for (const [name, sheet] of Object.entries(data.sheetDataByName || {})) {
        put(resourceKey(name), sheet, requestSequence);
        sheetDataByName[name] = get(resourceKey(name)) || sheet;
      }
      return { ...data, sheets: get(LIST_RESOURCE)?.sheets || [], sheetDataByName };
    }).finally(() => {
      if (snapshotFlight === read) snapshotFlight = null;
    });
    snapshotFlight = read;
    return read.promise;
  }

  function invalidateSheet(name) {
    const resource = resourceKey(name);
    versions.set(resource, (versions.get(resource) || 0) + 1);
    invalidationRevision += 1;
    snapshotFlight = null;
    entries.delete(resource);
    loadedResources.add(resource);
    inFlight.delete(resource);
    pendingWrites.delete(resource);
    if (session.persistent) removeStored(storedKey(resource));
  }

  function invalidateAll() {
    epoch += 1;
    invalidationRevision += 1;
    snapshotFlight = null;
    allowStorageHydration = false;
    entries.clear();
    inFlight.clear();
    versions.clear();
    pendingWrites.clear();
    if (writeTimer !== null) clearTimeout(writeTimer);
    writeTimer = null;
    if (session.persistent) {
      try {
        const storage = getStorage();
        if (storage) {
          for (const key of storageKeys(storage)) {
            if (key.startsWith(storageScope)) storage.removeItem(key);
          }
        }
      } catch {
        // Clearing memory still succeeds if storage access was revoked.
      }
    }
  }

  const api = {
    getSheets: () => get(LIST_RESOURCE),
    getSheet: (name) => get(resourceKey(name)),
    fetchSheets,
    fetchSheet,
    fetchSnapshot,
    invalidateAll,
    invalidateSheet,
  };
  caches.set(registryKey, {
    api,
    retire: () => {
      retired = true;
      invalidateAll();
    },
  });
  return api;
}

export function clearSheetCache() {
  for (const cache of caches.values()) cache.retire();
  caches.clear();
  try {
    const storage = getStorage();
    if (storage) {
      for (const key of storageKeys(storage)) storage.removeItem(key);
    }
  } catch {
    // Do not block logout when storage is unavailable.
  }
}
